import React, { useEffect, useMemo } from 'react';
import { useNavigate, NavLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Footer from '../footer/Footer';
import Testimonials from '../testimonials/Testimonials';
import seoData from '../../data/seoPages.json';

// ========================================
// KEYWORD-FORMATIERUNG FÜR DEUTSCHEN SATZBAU
// Kapitalisiert deutsche Substantive korrekt
// ========================================
const capitalizeKeyword = (keyword) => {
  if (!keyword) return '';
  
  // Wörter die im Deutschen kleingeschrieben bleiben (Präpositionen, Artikel, Verben, Adjektive etc.)
  const lowercaseWords = [
    'für', 'und', 'oder', 'in', 'aus', 'mit', 'von', 'bei', 'zu', 'der', 'die', 'das', 
    'ein', 'eine', 'durch', 'auf', 'an', 'um', 'über', 'unter', 'vor', 'nach', 'zwischen',
    'neue', 'neuen', 'neuer', 'aktuelle', 'aktuellen', 'täglich', 'ihre', 'ihr', 'ihren',
    'alle', 'allen', 'aller', 'komplette', 'intelligente', 'professionelle', 'ist', 'sind',
    'als', 'wie', 'wo', 'wer', 'was', 'welche', 'welcher', 'welches'
  ];
  
  // Wörter die immer groß geschrieben werden (deutsche Substantive, Fachbegriffe, Eigennamen)
  const alwaysCapitalize = [
    'stellendatenbank', 'jobdatenbank', 'arbeitsmarkt', 'jobmarkt', 'stellenmarkt',
    'recruiting', 'recruiter', 'personaldienstleister', 'personalberater', 'headhunter',
    'personalvermittlung', 'deutschland', 'europa', 'datenbank', 'software', 'tool',
    'platform', 'plattform', 'analyse', 'statistik', 'trends', 'trend', 'report',
    'leads', 'lead', 'vertrieb', 'akquise', 'neukunden', 'firmen', 'unternehmen',
    'aggregator', 'monitoring', 'tracking', 'insights', 'intelligence', 'hr', 'b2b',
    'saas', 'api', 'daten', 'jobanzeigen', 'stellenanzeigen', 'jobdaten', 'stellendaten',
    'auswertung', 'jobs', 'hiring', 'business', 'lösung', 'vorteile', 'millionen',
    'job-intelligence', 'marktanalyse', 'jobportal', 'jobportale'
  ];
  
  return keyword
    .split(' ')
    .map((word, index) => {
      const lowerWord = word.toLowerCase();
      
      // Erstes Wort immer großschreiben (Satzanfang)
      if (index === 0) {
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      }
      
      // Wörter die klein bleiben (Präpositionen, Artikel, etc.)
      if (lowercaseWords.includes(lowerWord)) {
        return lowerWord;
      }
      
      // Substantive und Fachbegriffe großschreiben
      if (alwaysCapitalize.some(term => lowerWord === term || lowerWord.includes(term))) {
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      }
      
      // Standard: NICHT großschreiben (sicherer für deutschen Satzbau)
      // Nur bekannte Substantive werden kapitalisiert
      return lowerWord;
    })
    .join(' ');
};

function SEOLandingPage({ pageData, category }) {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleDemoClick = () => {
    navigate('/demo');
  };

  // ========================================
  // PRIMARY KEYWORD EXTRACTION
  // Use first keyword from array, or fallback to single keyword for backward compatibility
  // ========================================
  const primaryKeyword = pageData.keywords?.[0] || pageData.keyword || '';

  // ========================================
  // SEITENTYP-ERKENNUNG
  // Hub-Seiten: 1000+ Wörter, FAQ, Übersicht
  // Programmatic: 400-600 Wörter, Region/Jobtyp-Fokus
  // Insights: Blog/Report-Stil, Trends
  // B2B-Leads: Conversion-fokussiert
  // ========================================
  const getPageType = () => {
    const keyword = primaryKeyword.toLowerCase();
    
    // Hub-Seiten: Hauptkategorien ohne spezifische Modifier
    const hubKeywords = [
      'stellendatenbank deutschland', 'jobdatenbank deutschland',
      'stellendatenbank europa', 'jobdatenbank europa'
    ];
    if (hubKeywords.includes(keyword)) return 'hub';
    
    // Insights-Seiten: Analyse, Statistik, Trends
    if (keyword.includes('analyse') || keyword.includes('statistik') || 
        keyword.includes('trend') || keyword.includes('arbeitsmarkt') ||
        keyword.includes('jobmarkt') || keyword.includes('insights')) {
      return 'insights';
    }
    
    // B2B-Leads-Seiten: Leads, Vertrieb, Akquise
    if (keyword.includes('lead') || keyword.includes('vertrieb') || 
        keyword.includes('akquise') || keyword.includes('neukunden') ||
        keyword.includes('b2b')) {
      return 'b2b-leads';
    }
    
    // Programmatic-Seiten: Alles andere (Region, Jobtyp, Branche)
    return 'programmatic';
  };

  const pageType = getPageType();

  // ========================================
  // VERWANDTE SEITEN FÜR INTERNE VERLINKUNG
  // ========================================
  const getRelatedPages = useMemo(() => {
    const allPages = [];
    Object.values(seoData.categories).forEach(cat => {
      cat.pages.forEach(page => {
        allPages.push({ ...page, category: cat });
      });
    });
    
    // Filtere verwandte Seiten aus derselben Kategorie
    const sameCategoryPages = category?.pages?.filter(p => p.slug !== pageData.slug).slice(0, 4) || [];
    
    // Finde Seiten mit ähnlichen Keywords
    const keyword = primaryKeyword.toLowerCase();
    const words = keyword.split(' ').filter(w => w.length > 3);
    
    const relatedFromOtherCategories = allPages
      .filter(p => {
        if (p.slug === pageData.slug) return false;
        // Support für neue Struktur: keywords als Array
        const pKeywords = Array.isArray(p.keywords) 
          ? p.keywords.map(k => k.toLowerCase()).join(' ')
          : (p.keyword || '').toLowerCase();
        return words.some(word => pKeywords.includes(word));
      })
      .slice(0, 4);
    
    return { sameCategoryPages, relatedFromOtherCategories };
  }, [pageData, category]);

  // ========================================
  // DYNAMISCHER CONTENT BASIEREND AUF KEYWORD
  // ========================================
  const generateContent = () => {
    const keyword = capitalizeKeyword(primaryKeyword);
    const keywordLower = primaryKeyword.toLowerCase();
    
    // Basis-Textbausteine
    const baseContent = {
      // Stellendatenbank Texte
      stellendatenbank: {
        intro: `Mit unserer ${keyword} erhalten Sie Zugang zu über 5 Millionen aktuellen Stellenanzeigen aus ganz Deutschland. Unsere Plattform aggregiert täglich neue Vakanzen aus allen relevanten Jobportalen wie StepStone, Indeed, LinkedIn, Xing und über 100 weiteren Quellen.`,
        mainText: `Die ${keyword} von Jobdaten ist die führende Lösung für professionelles Recruiting und B2B-Lead-Generierung im DACH-Raum. Personalberater, Headhunter, Personaldienstleister und HR-Teams nutzen unsere intelligente Datenbank, um Unternehmen mit aktivem Personalbedarf zu identifizieren und zum optimalen Zeitpunkt zu kontaktieren.

Unsere Stellendatenbank bietet Ihnen:
- Tägliche Aktualisierung aller Stellenanzeigen
- Intelligente Filterung nach Branche, Region und Jobtyp
- Direkte Kontaktdaten von HR-Entscheidern
- Export-Funktionen für Ihre CRM-Systeme
- DSGVO-konforme Datenverarbeitung in deutschen Rechenzentren

Im Gegensatz zu einzelnen Jobportalen sehen Sie bei uns das komplette Bild des Arbeitsmarktes. Sie verpassen keine Opportunity mehr und können Ihre Akquise-Aktivitäten strategisch planen.

Die Vorteile einer zentralen Stellendatenbank

Recruiter und Personalberater verbringen oft Stunden damit, verschiedene Jobportale zu durchsuchen. Mit unserer Stellendatenbank Deutschland sparen Sie diese Zeit und haben dennoch den vollständigen Marktüberblick. Alle Stellenanzeigen werden automatisch aggregiert, dedupliziert und mit Unternehmensdaten angereichert.

Für Personaldienstleister bietet unsere Plattform einen entscheidenden Wettbewerbsvorteil: Sie sehen Unternehmen mit aktivem Personalbedarf, bevor der Wettbewerb sie kontaktiert. Nutzen Sie Stellenanzeigen als Verkaufssignal und kontaktieren Sie potenzielle Kunden zum perfekten Zeitpunkt.

Daten für bessere Entscheidungen

Neben den Stellenanzeigen selbst erhalten Sie bei uns wertvolle Zusatzinformationen: Wie viele offene Stellen hat ein Unternehmen? Ist die Hiring-Aktivität steigend oder fallend? Welche Positionen werden am häufigsten ausgeschrieben? Diese Insights helfen Ihnen, Ihre Ressourcen auf die vielversprechendsten Opportunities zu konzentrieren.`,
        benefits: [
          { icon: '📊', title: 'Vollständige Marktabdeckung', desc: 'Alle deutschen Jobportale in einer zentralen Datenbank aggregiert. Kein manuelles Durchsuchen mehr.' },
          { icon: '🔄', title: 'Tägliche Aktualisierung', desc: 'Neue Stellenanzeigen innerhalb von 24 Stunden nach Veröffentlichung in unserem System.' },
          { icon: '🎯', title: 'Intelligente Filter', desc: 'Boolean-Suche, Branchen, Regionen, Unternehmensgrößen und viele weitere Filteroptionen.' },
          { icon: '📈', title: 'Hiring-Trends', desc: 'Analysieren Sie Wachstumsmuster und identifizieren Sie Unternehmen mit steigendem Bedarf.' }
        ],
        useCases: [
          { title: 'Personalberater & Headhunter', desc: 'Finden Sie Unternehmen mit aktivem Personalbedarf und kontaktieren Sie Entscheider genau dann, wenn sie bereit sind, Unterstützung anzunehmen.' },
          { title: 'Personaldienstleister & Zeitarbeit', desc: 'Identifizieren Sie Firmen mit saisonalem oder projektbezogenem Personalbedarf für Ihre Überlassungsangebote.' },
          { title: 'HR-Software & Services', desc: 'Nutzen Sie Stellenanzeigen als Verkaufssignal für Ihre HR-Tech-Lösungen, ATS oder Recruiting-Services.' }
        ]
      },
      
      // Jobdatenbank Texte
      jobdatenbank: {
        intro: `Die ${keyword} von Jobdaten ist Ihre zentrale Anlaufstelle für professionelle Recruiting-Intelligence. Mit über 5 Millionen Stellenanzeigen und täglichen Updates haben Sie den kompletten deutschen Arbeitsmarkt im Blick.`,
        mainText: `Unsere ${keyword} revolutioniert die Art, wie Recruiting-Professionals und B2B-Vertriebsteams arbeiten. Anstatt stundenlang einzelne Jobportale zu durchsuchen, erhalten Sie alle relevanten Informationen an einem Ort.

Die Jobdatenbank ist mehr als nur eine Sammlung von Stellenanzeigen. Sie ist ein strategisches Tool für:

Lead-Generierung: Unternehmen, die aktiv einstellen, haben Budget und sind bereit zu investieren. Perfekte Verkaufssignale für Ihre B2B-Angebote.

Wettbewerbsanalyse: Verfolgen Sie, welche Positionen Ihre Wettbewerber ausschreiben und welche Skills nachgefragt werden.

Marktbeobachtung: Erkennen Sie Trends, bevor sie zum Mainstream werden. Wachsende Branchen, neue Technologien, regionale Entwicklungen.

Automatisierte Alerts: Lassen Sie sich informieren, wenn relevante neue Stellenanzeigen erscheinen. Nie wieder eine Opportunity verpassen.

Warum eine zentrale Jobdatenbank?

Der fragmentierte Arbeitsmarkt mit dutzenden Jobportalen macht es schwer, den Überblick zu behalten. Unsere Jobdatenbank Deutschland löst dieses Problem, indem sie alle Quellen zusammenführt und intelligent aufbereitet.

Für Recruiter bedeutet das: weniger Zeitaufwand für Recherche, mehr Zeit für wertschöpfende Tätigkeiten wie Kandidatengespräche und Kundenbetreuung. Für Vertriebsteams heißt das: qualifizierte Leads auf Knopfdruck, statt stundenlanger manueller Recherche.

Integration in Ihre Workflows

Unsere Jobdatenbank lässt sich nahtlos in Ihre bestehenden Systeme integrieren. Exportieren Sie Daten im CSV- oder Excel-Format für Ihre CRM-Systeme, oder nutzen Sie unsere API für automatisierte Workflows. So wird die Jobdatenbank zum zentralen Element Ihrer Akquise-Strategie.`,
        benefits: [
          { icon: '🌐', title: 'Alle Jobportale vereint', desc: 'StepStone, Indeed, LinkedIn, Xing, Monster und 100+ weitere Quellen in einer Oberfläche.' },
          { icon: '⚡', title: 'Echtzeit-Updates', desc: 'Neue Vakanzen sind meist innerhalb weniger Stunden nach Veröffentlichung verfügbar.' },
          { icon: '🔔', title: 'Smarte Alerts', desc: 'Automatische Benachrichtigungen bei neuen relevanten Stellenanzeigen nach Ihren Kriterien.' },
          { icon: '📥', title: 'Flexible Exporte', desc: 'CSV, Excel und API-Integration für nahtlose Anbindung an Ihre bestehenden Systeme.' }
        ],
        useCases: [
          { title: 'Recruiting-Agenturen', desc: 'Kompletter Überblick über alle offenen Vakanzen Ihrer Zielkunden. Identifizieren Sie Firmen mit akutem Bedarf.' },
          { title: 'Business Development', desc: 'Stellenanzeigen als Sales-Trigger nutzen. Unternehmen, die einstellen, investieren auch in andere Bereiche.' },
          { title: 'Market Research', desc: 'Datenbasierte Marktanalysen. Welche Skills werden nachgefragt? Welche Regionen wachsen?' }
        ]
      },
      
      // Recruiting Leads Texte
      recruitingLeads: {
        intro: `${keyword} - Generieren Sie täglich qualifizierte B2B-Leads aus Millionen von Stellenanzeigen. Unternehmen mit aktivem Personalbedarf haben Budget, Dringlichkeit und sind bereit, Unterstützung anzunehmen.`,
        mainText: `Mit ${keyword} von Jobdaten nutzen Sie die mächtigste Informationsquelle für B2B-Leads im HR-Bereich: Stellenanzeigen. Jede offene Stelle ist ein Signal, dass ein Unternehmen investiert und wächst.

Warum Stellenanzeigen die besten Verkaufssignale sind:

Unternehmen, die Personal suchen, haben typischerweise:
- Freigegebenes Budget für Neueinstellungen
- Dringlichkeit, offene Positionen zu besetzen
- Bereitschaft, mit externen Partnern zusammenzuarbeiten
- Wachstumspläne, die weitere Investitionen nach sich ziehen

So nutzen erfolgreiche B2B-Teams unsere Daten:

1. Identifizieren: Finden Sie täglich neue Unternehmen mit relevantem Personalbedarf
2. Qualifizieren: Filtern Sie nach Branche, Größe, Region und Anzahl offener Stellen
3. Kontaktieren: Nutzen Sie unsere Ansprechpartner-Daten für zielgerichtete Ansprache
4. Timing: Erreichen Sie Entscheider genau dann, wenn der Bedarf akut ist

Sparen Sie 15-20 Stunden pro Woche bei der manuellen Recherche und konzentrieren Sie sich auf das, was wirklich zählt: Gespräche mit potenziellen Kunden.

Lead-Qualifizierung automatisieren

Mit unseren Filtern und Lead-Scoring-Funktionen qualifizieren Sie Leads automatisch. Unternehmen mit vielen offenen Stellen, steigender Hiring-Aktivität oder bestimmten Stellenprofilen können priorisiert werden. So fokussieren Sie Ihre Vertriebsressourcen auf die vielversprechendsten Opportunities.

Der perfekte Zeitpunkt für den Erstkontakt

Timing ist im B2B-Vertrieb entscheidend. Wenn ein Unternehmen gerade eine Stelle ausschreibt, ist der Bedarf akut. Nutzen Sie dieses Zeitfenster für Ihre Ansprache – die Wahrscheinlichkeit für ein positives Gespräch ist deutlich höher als bei Kaltakquise ohne konkreten Anlass.`,
        benefits: [
          { icon: '🎯', title: 'Qualifizierte Leads', desc: 'Jeder Lead hat einen nachweisbaren Personalbedarf. Perfekte Verkaufssignale statt Kaltakquise.' },
          { icon: '⏰', title: 'Perfektes Timing', desc: 'Kontaktieren Sie Unternehmen genau dann, wenn sie aktiv nach Lösungen suchen.' },
          { icon: '💼', title: 'Entscheider-Kontakte', desc: 'Direkte Ansprechpartner in HR, Recruiting, Geschäftsführung mit E-Mail und Telefon.' },
          { icon: '📊', title: 'Lead-Scoring', desc: 'Priorisieren Sie Leads nach Hiring-Aktivität, Unternehmenswachstum und Relevanz.' }
        ],
        useCases: [
          { title: 'Sales-Teams', desc: 'Sparen Sie Zeit bei der Lead-Recherche. Kontaktieren Sie nur Unternehmen mit nachgewiesenem Bedarf.' },
          { title: 'Business Development', desc: 'Identifizieren Sie wachsende Unternehmen und Marktchancen in Echtzeit.' },
          { title: 'Account Manager', desc: 'Überwachen Sie Hiring-Aktivitäten Ihrer Bestandskunden für Upselling und Cross-Selling.' }
        ]
      },
      
      // Firmen die einstellen Texte
      firmenEinstellen: {
        intro: `Finden Sie ${keyword} mit unserer intelligenten Datenbank. Täglich aktualisierte Informationen zu Unternehmen mit aktivem Personalbedarf, Wachstumspotenzial und direkten Ansprechpartnern.`,
        mainText: `Mit unserer Plattform identifizieren Sie ${keyword} in Sekundenschnelle. Wir aggregieren Stellenanzeigen aus allen deutschen Jobportalen und reichern sie mit Unternehmensdaten an.

Was Sie über jedes Unternehmen erfahren:

- Anzahl aktuell offener Stellen
- Historische Hiring-Aktivität (wächst das Unternehmen?)
- Gesuchte Positionen und Skills
- Branche und Unternehmensgröße
- Direkte Ansprechpartner in HR und Management

Filtern Sie nach Ihren Kriterien:

Sie suchen IT-Firmen, die Entwickler einstellen? Logistikunternehmen mit Fahrerbedarf? Pflegeeinrichtungen mit offenen Stellen? Mit unseren intelligenten Filtern finden Sie genau die Unternehmen, die für Ihr Angebot relevant sind.

Regionale und branchenspezifische Suche:

Ob München, Hamburg, Berlin oder ländliche Regionen – unsere Datenbank deckt ganz Deutschland ab. Kombinieren Sie Standort-Filter mit Branchen und Job-Kategorien für präzise Ergebnisse.

Wachsende Unternehmen identifizieren

Firmen mit steigender Hiring-Aktivität sind besonders interessante Ziele für B2B-Anbieter. Sie wachsen, investieren und sind offen für neue Partnerschaften. Unsere Plattform zeigt Ihnen genau diese Wachstumssignale.`,
        benefits: [
          { icon: '🏢', title: 'Umfassende Firmendatenbank', desc: 'Über 1 Million deutsche Unternehmen mit detaillierten Informationen und Kontaktdaten.' },
          { icon: '📈', title: 'Wachstumsindikatoren', desc: 'Erkennen Sie Unternehmen mit steigendem Personalbedarf und Expansionsplänen.' },
          { icon: '🔍', title: 'Flexible Filterung', desc: 'IT, Vertrieb, Pflege, Handwerk – über 50 Branchen und Job-Kategorien.' },
          { icon: '📍', title: 'Regionale Suche', desc: 'Alle Bundesländer, Städte und PLZ-Gebiete für lokale oder überregionale Akquise.' }
        ],
        useCases: [
          { title: 'IT-Recruiting', desc: 'Firmen, die Entwickler, DevOps, Data Scientists und IT-Spezialisten suchen – alle in einer Übersicht.' },
          { title: 'Vertrieb & Sales', desc: 'Unternehmen mit Bedarf an Vertriebsmitarbeitern, Account Managern und Sales-Profis.' },
          { title: 'Pflege & Gesundheit', desc: 'Kliniken, Pflegeheime, Arztpraxen und Gesundheitseinrichtungen mit Personalbedarf.' }
        ]
      },
      
      // Arbeitsmarkt/Insights Texte
      arbeitsmarkt: {
        intro: `${keyword} für strategische Entscheidungen. Nutzen Sie datenbasierte Insights aus Millionen von Stellenanzeigen für fundierte Marktanalysen, Standortentscheidungen und Recruiting-Strategien.`,
        mainText: `Die ${keyword} von Jobdaten basiert auf der Analyse von über 5 Millionen Stellenanzeigen. Wir tracken täglich, welche Unternehmen einstellen, welche Skills nachgefragt werden und wie sich regionale Arbeitsmärkte entwickeln.

Aktuelle Trends und Erkenntnisse:

Der deutsche Arbeitsmarkt zeigt dynamische Entwicklungen. Während einige Branchen mit Fachkräftemangel kämpfen, wachsen andere Bereiche überdurchschnittlich. Unsere Daten zeigen:

- IT & Tech: Weiterhin hohe Nachfrage nach Entwicklern, Cloud-Experten und Data Scientists
- Pflege & Gesundheit: Chronisch unterbesetzt, hohe Fluktuation, viele offene Stellen
- Vertrieb: Wachsende Nachfrage, insbesondere im B2B-Bereich
- Logistik: Saisonale Schwankungen, aber grundsätzlich starker Bedarf

Nutzen Sie unsere Daten für:

1. Standortanalysen: Welche Regionen haben die meisten offenen Stellen in Ihrer Branche?
2. Wettbewerbsbeobachtung: Welche Positionen schreiben Ihre Wettbewerber aus?
3. Gehaltsrecherche: Welche Gehälter werden für bestimmte Positionen genannt?
4. Skill-Trends: Welche Technologien und Fähigkeiten werden verstärkt nachgefragt?

Regionale Arbeitsmarktanalysen

Jede Region hat ihre eigene Arbeitsmarktdynamik. Während Metropolen wie München, Berlin oder Hamburg in bestimmten Branchen dominieren, gibt es in anderen Regionen unentdeckte Potenziale. Unsere Analysen helfen Ihnen, diese Unterschiede zu verstehen und für Ihre Strategie zu nutzen.

Datenbasierte Entscheidungen

Ob Sie eine Expansion planen, einen neuen Markt erschließen oder Ihre Recruiting-Strategie optimieren wollen – fundierte Daten sind die Basis für gute Entscheidungen. Mit unseren Arbeitsmarkt-Insights haben Sie diese Daten immer zur Hand.`,
        benefits: [
          { icon: '📊', title: 'Markt-Intelligence', desc: 'Echtzeit-Daten zu Hiring-Trends, Nachfrageentwicklung und regionalen Unterschieden.' },
          { icon: '📈', title: 'Trend-Analysen', desc: 'Erkennen Sie Marktentwicklungen frühzeitig und positionieren Sie sich strategisch.' },
          { icon: '🗺️', title: 'Regionale Insights', desc: 'Vergleichen Sie Arbeitsmärkte nach Bundesländern, Städten und Wirtschaftsregionen.' },
          { icon: '📋', title: 'Custom Reports', desc: 'Erstellen Sie professionelle Analysen für Management, Kunden oder Investoren.' }
        ],
        useCases: [
          { title: 'Marktforschung', desc: 'Datenbasierte Analysen zu Branchen, Wettbewerbern und Marktpotenzial basierend auf Hiring-Aktivitäten.' },
          { title: 'Strategische Planung', desc: 'Fundierte Entscheidungsgrundlagen für Expansion, Standortwahl und Personalplanung.' },
          { title: 'Investmentanalyse', desc: 'Bewerten Sie Wachstumspotenzial von Branchen und Unternehmen anhand ihrer Hiring-Aktivität.' }
        ]
      }
    };
    
    // Content-Auswahl basierend auf Keyword
    if (keywordLower.includes('stellendatenbank')) {
      return baseContent.stellendatenbank;
    }
    if (keywordLower.includes('jobdatenbank')) {
      return baseContent.jobdatenbank;
    }
    if (keywordLower.includes('lead') || keywordLower.includes('b2b') || keywordLower.includes('recruiting lead')) {
      return baseContent.recruitingLeads;
    }
    if (keywordLower.includes('firmen') || keywordLower.includes('unternehmen')) {
      return baseContent.firmenEinstellen;
    }
    if (keywordLower.includes('arbeitsmarkt') || keywordLower.includes('analyse') || keywordLower.includes('statistik') || keywordLower.includes('trend')) {
      return baseContent.arbeitsmarkt;
    }
    
    // Default Content
    return baseContent.stellendatenbank;
  };

  const content = generateContent();

  // ========================================
  // DYNAMISCHER TEXT FÜR PROGRAMMATIC PAGES
  // Beispiel: /firmen-die-softwareentwickler-suchen-berlin
  // ========================================
  const generateDynamicText = () => {
    const keyword = capitalizeKeyword(primaryKeyword);
    const keywordLower = primaryKeyword.toLowerCase();
    
    // Extrahiere Stadt/Region wenn vorhanden
    const cities = ['berlin', 'münchen', 'hamburg', 'köln', 'frankfurt', 'düsseldorf', 'stuttgart', 'leipzig', 'dresden', 'nürnberg', 'hannover', 'bremen', 'essen', 'dortmund', 'karlsruhe', 'mannheim', 'augsburg', 'wiesbaden', 'bonn', 'münster'];
    const foundCity = cities.find(city => keywordLower.includes(city));
    const cityName = foundCity ? foundCity.charAt(0).toUpperCase() + foundCity.slice(1) : null;
    
    // Extrahiere Jobtyp wenn vorhanden
    const jobTypes = {
      'softwareentwickler': 'Softwareentwickler',
      'entwickler': 'Entwickler',
      'it': 'IT-Fachkräfte',
      'vertrieb': 'Vertriebsmitarbeiter',
      'sales': 'Sales-Profis',
      'pflege': 'Pflegekräfte',
      'handwerk': 'Handwerker',
      'marketing': 'Marketing-Experten',
      'hr': 'HR-Fachkräfte',
      'recruiting': 'Recruiter',
      'fahrer': 'Fahrer',
      'logistik': 'Logistik-Fachkräfte'
    };
    const foundJobKey = Object.keys(jobTypes).find(job => keywordLower.includes(job));
    const foundJobType = foundJobKey ? jobTypes[foundJobKey] : null;
    
    if (cityName && foundJobType) {
      return {
        localIntro: `In ${cityName} suchen aktuell zahlreiche Unternehmen nach qualifizierten ${foundJobType}. Unsere Jobdatenbank aggregiert alle Stellenanzeigen aus den wichtigsten Portalen, sodass Personalberater, Recruiter und HR-Teams schnell die Firmen mit offenem Bedarf identifizieren können.`,
        bullets: [
          `Alle aktuellen ${foundJobType}-Stellen in ${cityName} auf einen Blick`,
          'Firmen nach Branche oder Unternehmensgröße filtern',
          'Leads für Ihre Kundenakquise generieren',
          'Direkte Kontaktdaten von HR-Entscheidern'
        ],
        cta: `Nutzen Sie unsere Software, um keine offenen ${foundJobType}-Stellen in ${cityName} zu verpassen und neue Kunden für Ihre Personalvermittlung zu gewinnen.`
      };
    }
    
    if (cityName) {
      return {
        localIntro: `Der Arbeitsmarkt in ${cityName} ist dynamisch und bietet zahlreiche Chancen für Recruiting-Professionals. Mit unserer ${keyword}-Lösung sehen Sie alle offenen Stellen in der Region und identifizieren Unternehmen mit aktivem Personalbedarf.`,
        bullets: [
          `Alle Stellenanzeigen aus ${cityName} und Umgebung`,
          'Unternehmen mit Wachstum und Hiring-Aktivität',
          'Direkte Ansprechpartner für Ihre Akquise',
          'Tägliche Updates für aktuelle Daten'
        ],
        cta: `Starten Sie jetzt und finden Sie die besten Recruiting-Opportunities in ${cityName}.`
      };
    }
    
    if (foundJobType) {
      return {
        localIntro: `Deutschlandweit suchen Unternehmen nach ${foundJobType}. Mit unserer Jobdatenbank finden Sie alle aktuellen Vakanzen und die Firmen dahinter – perfekt für gezielte Kundenakquise.`,
        bullets: [
          `Alle offenen ${foundJobType}-Stellen deutschlandweit`,
          'Firmen mit aktivem Personalbedarf identifizieren',
          'Wachsende Unternehmen früh erkennen',
          'Kontaktdaten für direkte Ansprache'
        ],
        cta: `Verpassen Sie keine Opportunity im ${foundJobType}-Markt. Testen Sie unsere Plattform kostenlos.`
      };
    }
    
    return null; // Kein lokaler Content nötig
  };

  const dynamicText = pageType === 'programmatic' ? generateDynamicText() : null;

  // ========================================
  // STATISTIKEN
  // ========================================
  const stats = [
    { value: '5.000.000+', label: 'Stellenanzeigen' },
    { value: '400.000+', label: 'Unternehmen' },
    { value: '400.000+', label: 'Aktive Vakanzen pro Monat' },
    { value: '100+', label: 'Jobportale' }
  ];

  // ========================================
  // STRUCTURED DATA / SCHEMA MARKUP
  // ========================================
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": pageData.title,
    "description": pageData.metaDescription,
    "url": `https://jobdaten.com/${pageData.slug}`,
    "inLanguage": "de-DE",
    "isPartOf": {
      "@type": "WebSite",
      "name": "Jobdaten",
      "url": "https://jobdaten.com"
    },
    "about": {
      "@type": "SoftwareApplication",
      "name": "Jobdaten",
      "applicationCategory": "BusinessApplication",
      "description": "B2B Lead-Generierung und Recruiting-Intelligence Plattform",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "EUR",
        "description": "Demo kostenlos testen"
      }
    },
    "provider": {
      "@type": "Organization",
      "name": "Diligent Recruitment GmbH",
      "url": "https://jobdaten.com",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Faberstrasse 11",
        "addressLocality": "Rheinstetten",
        "postalCode": "76287",
        "addressCountry": "DE"
      },
      "telephone": "+49 721 75401610"
    },
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://jobdaten.com" },
        { "@type": "ListItem", "position": 2, "name": category?.title || 'Seiten', "item": `https://jobdaten.com/${category?.slug || ''}` },
        { "@type": "ListItem", "position": 3, "name": capitalizeKeyword(pageData.h1), "item": `https://jobdaten.com/${pageData.slug}` }
      ]
    }
  };

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": `Was ist ${capitalizeKeyword(primaryKeyword)}?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": `${capitalizeKeyword(primaryKeyword)} von Jobdaten ist eine intelligente Plattform für B2B-Lead-Generierung und Recruiting-Intelligence. Mit Zugang zu über 5 Millionen Stellenanzeigen von 400.000+ Unternehmen können Sie täglich Unternehmen mit aktivem Personalbedarf identifizieren.`
        }
      },
      {
        "@type": "Question",
        "name": "Wie aktuell sind die Daten?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Unsere Datenbank wird täglich aktualisiert. Neue Stellenanzeigen sind meist innerhalb von 24 Stunden nach Veröffentlichung verfügbar."
        }
      },
      {
        "@type": "Question",
        "name": "Für welche Branchen ist die Plattform geeignet?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Unsere Plattform eignet sich für alle Branchen. Sie können nach IT, Vertrieb, Pflege, Handwerk, Finanzen und über 50 weiteren Kategorien filtern."
        }
      },
      {
        "@type": "Question", 
        "name": "Ist die Nutzung DSGVO-konform?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Ja, vollständig. Jobdaten speichert alle Daten in deutschen Rechenzentren und erfüllt alle DSGVO-Anforderungen."
        }
      }
    ]
  };

  // ========================================
  // RENDER PAGE
  // ========================================
  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{pageData.title}</title>
        <meta name="description" content={pageData.metaDescription} />
        <meta name="keywords" content={`${pageData.keywords?.join(', ') || primaryKeyword}, Jobdaten, Recruiting, Lead-Generierung, Stellendatenbank, Jobdatenbank, HR-Tech, Personaldienstleister, B2B Leads`} />
        <meta name="author" content="Jobdaten - Diligent Recruitment GmbH" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`https://jobdaten.com/${pageData.slug}`} />
        
        {/* Open Graph */}
        <meta property="og:title" content={pageData.title} />
        <meta property="og:description" content={pageData.metaDescription} />
        <meta property="og:url" content={`https://jobdaten.com/${pageData.slug}`} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Jobdaten" />
        <meta property="og:locale" content="de_DE" />
        <meta property="og:image" content="https://jobdaten.com/images/og-image.jpg" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageData.title} />
        <meta name="twitter:description" content={pageData.metaDescription} />
        
        {/* Structured Data */}
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
        <script type="application/ld+json">{JSON.stringify(faqStructuredData)}</script>
      </Helmet>

      <div className="w-full min-h-screen bg-white">
        {/* ========================================
            NAVBAR
        ======================================== */}
        <nav className="w-full h-[80px] bg-white shadow-sm sticky top-0 z-50">
          <div className="project-container flex items-center justify-between py-6 px-4">
            <NavLink to="/">
              <img src="/images/logo-black.svg" alt="Jobdaten Logo" className="w-[180px]" />
            </NavLink>
            <div className="flex items-center gap-4">
              <NavLink to="/login" className="hidden sm:block text-gray-600 hover:text-[#B588FF] transition-colors">
                Login
              </NavLink>
              <button onClick={handleDemoClick} className="px-6 py-2 rounded-full bg-gradient-to-r from-[#B588FF] to-[#8B5FFF] text-white font-medium transform transition-all duration-300 hover:scale-105 hover:shadow-lg">
                Demo anfordern
              </button>
            </div>
          </div>
        </nav>

        {/* ========================================
            BREADCRUMB
        ======================================== */}
        <div className="project-container px-4 py-4">
          <nav className="text-sm text-gray-500" aria-label="Breadcrumb">
            <ol className="flex flex-wrap items-center gap-1">
              <li><NavLink to="/" className="hover:text-[#B588FF]">Home</NavLink></li>
              <li><span className="mx-2">/</span></li>
              {category && (
                <>
                  <li><NavLink to={`/${category.slug}`} className="hover:text-[#B588FF]">{category.title}</NavLink></li>
                  <li><span className="mx-2">/</span></li>
                </>
              )}
              <li className="text-gray-700 truncate max-w-[200px]">{capitalizeKeyword(pageData.h1)}</li>
            </ol>
          </nav>
        </div>

        {/* ========================================
            HERO SECTION + CTA OBEN
            "Jetzt Demo anfragen"
        ======================================== */}
        <section className="w-full md:mt-6 mt-4 md:mb-16 mb-10">
          <div className="project-container px-4">
            <div className="flex items-start justify-between flex-col lg:flex-row gap-10">
              {/* Left Content */}
              <div className="w-full lg:w-7/12">
                <h1 className="xl:text-[48px] xl:leading-[130%] md:text-4xl text-3xl font-bold text-black text-center lg:text-left">
                  {capitalizeKeyword(pageData.h1)}
                </h1>
                
                <p className="mt-6 text-lg md:text-xl text-gray-600 lg:text-left text-center leading-relaxed">
                  {pageData.uniqueIntro || content.intro}
                </p>
                
                {/* Statistik-Highlight */}
                {pageData.uniqueStat && (
                  <p className="mt-3 text-sm font-medium text-[#B588FF] lg:text-left text-center">
                    📊 {pageData.uniqueStat}
                  </p>
                )}

                {/* CTA OBEN - "Jetzt Demo anfragen" */}
                <div className="mt-8 flex flex-col sm:flex-row justify-center lg:justify-start gap-4">
                  <button onClick={handleDemoClick} className="px-8 py-3 rounded-full bg-gradient-to-r from-[#B588FF] to-[#8B5FFF] text-white font-semibold transform transition-all duration-300 hover:scale-105 hover:shadow-2xl active:scale-95 text-center">
                    Jetzt Demo anfragen
                  </button>
                  <button onClick={() => navigate('/demo')} className="px-8 py-3 rounded-full bg-white border-2 border-[#D4D4D4] font-medium transform transition-all duration-300 hover:scale-105 hover:border-[#B588FF] hover:text-[#B588FF] active:scale-95 text-center">
                    Kostenlos testen
                  </button>
                </div>

                {/* Trust Badges */}
                <div className="mt-8 flex flex-wrap gap-3 justify-center lg:justify-start">
                  {pageData.uniqueBenefits && pageData.uniqueBenefits.length > 0 ? (
                    pageData.uniqueBenefits.map((benefit, idx) => (
                      <span key={idx} className="px-4 py-2 bg-gray-100 rounded-full text-sm text-gray-600">✓ {benefit}</span>
                    ))
                  ) : (
                    <>
                      <span className="px-4 py-2 bg-gray-100 rounded-full text-sm text-gray-600">✓ 5 Mio+ Stellenanzeigen</span>
                      <span className="px-4 py-2 bg-gray-100 rounded-full text-sm text-gray-600">✓ Kostenloser Demo Termin</span>
                      <span className="px-4 py-2 bg-gray-100 rounded-full text-sm text-gray-600">✓ Alle Features</span>
                    </>
                  )}
                </div>
              </div>

              {/* Right Video */}
              <div className="w-full lg:w-5/12">
                <div className="relative w-full pt-[56.25%] rounded-xl overflow-hidden shadow-2xl">
                  <video autoPlay loop muted playsInline preload="none" className="absolute inset-0 w-full h-full object-cover">
                    <source src="/images/Design-ohne-Titel-1.mp4" type="video/mp4" />
                  </video>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ========================================
            KURZBESCHREIBUNG (2-3 Sätze)
            Was bietet die Software / Datenbank?
        ======================================== */}
        <section className="w-full py-12 bg-gray-50">
          <div className="project-container px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold mb-6">{capitalizeKeyword(pageData.h2) || `Vorteile der ${capitalizeKeyword(primaryKeyword)}`}</h2>
              
              {/* Dynamischer lokaler Text für Programmatic Pages */}
              {dynamicText && (
                <div className="mb-8 p-6 bg-white rounded-xl border border-gray-200">
                  <p className="text-gray-700 leading-relaxed mb-4">{dynamicText.localIntro}</p>
                  <h3 className="font-bold text-lg mb-3">Vorteile für Recruiter:</h3>
                  <ul className="space-y-2 mb-4">
                    {dynamicText.bullets.map((bullet, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-gray-600">
                        <span className="text-[#B588FF] mt-1">✓</span>
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-gray-700 italic">{dynamicText.cta}</p>
                </div>
              )}
              
              {/* Main Text - längerer Content für Hub-Seiten */}
              <div className="prose prose-lg max-w-none text-gray-700">
                {content.mainText.split('\n\n').map((paragraph, idx) => {
                  // Überschriften
                  if (paragraph.match(/^[A-ZÄÖÜ].*:$/)) {
                    return <h3 key={idx} className="text-xl font-bold mt-8 mb-4 text-black">{paragraph.replace(':', '')}</h3>;
                  }
                  // Listen mit -
                  if (paragraph.startsWith('- ')) {
                    return (
                      <ul key={idx} className="list-none space-y-2 my-4">
                        {paragraph.split('\n').map((item, i) => (
                          <li key={i} className="flex items-start gap-3">
                            <span className="text-[#B588FF]">•</span>
                            <span>{item.replace('- ', '')}</span>
                          </li>
                        ))}
                      </ul>
                    );
                  }
                  // Nummerierte Listen
                  if (paragraph.match(/^1\./)) {
                    return (
                      <ol key={idx} className="list-decimal list-inside space-y-2 my-4 pl-4">
                        {paragraph.split('\n').map((item, i) => (
                          <li key={i} className="text-gray-700">{item.replace(/^\d+\.\s*/, '')}</li>
                        ))}
                      </ol>
                    );
                  }
                  // Normaler Absatz
                  return <p key={idx} className="mb-4 leading-relaxed">{paragraph}</p>;
                })}
              </div>
            </div>
          </div>
        </section>

        {/* ========================================
            FEATURES / VORTEILE
            Bulletpoints oder Icons
        ======================================== */}
        <section className="w-full py-16">
          <div className="project-container px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
              Daten aus allen Jobportalen
            </h2>
            <p className="text-lg text-gray-500 text-center mb-12 max-w-2xl mx-auto">
              Alle Vorteile von {capitalizeKeyword(primaryKeyword)} auf einen Blick
            </p>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {content.benefits.map((benefit, index) => (
                <div key={index} className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-lg transition-shadow">
                  <div className="text-4xl mb-4">{benefit.icon}</div>
                  <h3 className="text-lg font-bold mb-2 text-black">{benefit.title}</h3>
                  <p className="text-gray-600 text-sm">{benefit.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ========================================
            STATISTIKEN / INSIGHTS
            "500.000 offene Stellen analysiert"
        ======================================== */}
        <section className="w-full py-16 bg-gradient-to-r from-[#B588FF] to-[#8B5FFF] text-white">
          <div className="project-container px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-10">
              Jobdaten in Zahlen
            </h2>
            <div className="flex flex-row justify-around items-start gap-4 md:gap-8 text-center">
              {stats.map((stat, idx) => (
                <div key={idx} className="flex-1">
                  <div className="text-xl md:text-5xl font-bold mb-2">{stat.value}</div>
                  <p className="text-white/80 text-[10px] md:text-base leading-tight">{stat.label}</p>
                </div>
              ))}
            </div>
            
            {/* CTA MITTIG - "Jobdatenbank kostenlos testen" */}
            <div className="mt-12 text-center">
              <button onClick={handleDemoClick} className="px-10 py-4 bg-white rounded-full font-bold text-lg transform transition-all duration-300 hover:scale-105 hover:shadow-2xl" style={{ color: '#8B5FFF' }}>
                Jobdaten Demo testen
              </button>
            </div>
          </div>
        </section>

        {/* ========================================
            TESTIMONIALS / KUNDENSTIMMEN
        ======================================== */}
        <Testimonials />

        {/* ========================================
            FAQ SECTION - UNIQUE PER PAGE
            Keyword-spezifische FAQs
        ======================================== */}
        <section className="w-full py-16 bg-gray-50">
          <div className="project-container px-4 max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              Häufige Fragen zu {capitalizeKeyword(primaryKeyword)}
            </h2>
            
            <div className="space-y-4">
              {/* Unique FAQs from pageData */}
              {pageData.uniqueFAQs && pageData.uniqueFAQs.map((faq, idx) => (
                <details key={idx} className="bg-white p-6 rounded-xl shadow-sm group" open={idx === 0}>
                  <summary className="text-lg font-bold cursor-pointer flex items-center justify-between">
                    {faq.q}
                    <span className="text-[#B588FF] group-open:rotate-180 transition-transform">▼</span>
                  </summary>
                  <p className="text-gray-600 mt-4">{faq.a}</p>
                </details>
              ))}
              
              {/* Standard FAQs als Fallback */}
              <details className="bg-white p-6 rounded-xl shadow-sm group">
                <summary className="text-lg font-bold cursor-pointer flex items-center justify-between">
                  Wie aktuell sind die Daten?
                  <span className="text-[#B588FF] group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <p className="text-gray-600 mt-4">
                  Unsere Datenbank wird täglich aktualisiert. Neue Stellenanzeigen sind meist innerhalb von 24 Stunden nach Veröffentlichung in unserem System verfügbar.
                </p>
              </details>
              
              <details className="bg-white p-6 rounded-xl shadow-sm group">
                <summary className="text-lg font-bold cursor-pointer flex items-center justify-between">
                  Ist die Nutzung DSGVO-konform?
                  <span className="text-[#B588FF] group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <p className="text-gray-600 mt-4">
                  Ja, vollständig. Alle Daten werden ausschließlich in deutschen Rechenzentren gespeichert und wir erfüllen alle DSGVO-Anforderungen.
                </p>
              </details>
            </div>
          </div>
        </section>

        {/* ========================================
            INTERNE VERLINKUNG
            Weitere Unterseiten / relevante Hubs
        ======================================== */}
        {getRelatedPages.sameCategoryPages.length > 0 && (
          <section className="w-full py-16">
            <div className="project-container px-4">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                Weitere Seiten zu {category?.title || 'Jobdaten'}
              </h2>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {getRelatedPages.sameCategoryPages.map((page, idx) => (
                  <NavLink 
                    key={idx} 
                    to={`/${page.slug}`}
                    className="p-4 bg-white rounded-lg border border-gray-200 hover:border-[#B588FF] hover:shadow-md transition-all"
                  >
                    <h3 className="font-medium text-gray-800 hover:text-[#B588FF] capitalize">{capitalizeKeyword(page.keywords?.[0] || page.keyword || page.slug)}</h3>
                    <span className="text-sm text-gray-500">→ Mehr erfahren</span>
                  </NavLink>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Verwandte Themen aus anderen Kategorien */}
        {getRelatedPages.relatedFromOtherCategories.length > 0 && (
          <section className="w-full py-12 bg-gray-50">
            <div className="project-container px-4">
              <h2 className="text-xl md:text-2xl font-bold text-center mb-6">
                Verwandte Themen
              </h2>
              
              <div className="flex flex-wrap justify-center gap-3">
                {getRelatedPages.relatedFromOtherCategories.map((page, idx) => (
                  <NavLink 
                    key={idx} 
                    to={`/${page.slug}`}
                    className="px-4 py-2 bg-white rounded-full border border-gray-200 hover:border-[#B588FF] hover:text-[#B588FF] text-sm transition-all"
                  >
                    {capitalizeKeyword(page.keywords?.[0] || page.keyword || page.slug)}
                  </NavLink>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ========================================
            CTA AM ENDE + FORMULAR
            "Kontakt für Recruiting Leads aufnehmen"
        ======================================== */}
        <section className="w-full py-20 bg-gray-50">
          <div className="project-container px-4 text-center">
            <h2 className="text-3xl md:text-5xl font-bold mb-6">
              Kontakt für {capitalizeKeyword(primaryKeyword)}
            </h2>
            <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto">
              Starten Sie jetzt mit einem kostenlosen Demo Termin. Finden Sie Ihre Zielkunden und machen ab sofort mehr Umsatz.
            </p>
            
            {/* CTA AM ENDE */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={handleDemoClick} className="px-10 py-4 bg-gradient-to-r from-[#B588FF] to-[#8B5FFF] rounded-full font-semibold text-xl text-white transform transition-all duration-300 hover:scale-105 hover:shadow-2xl">
                Kostenlose Demo anfordern
              </button>
              <NavLink to="/login" className="px-10 py-4 bg-white border-2 border-gray-300 rounded-full font-semibold text-xl text-gray-700 transform transition-all duration-300 hover:scale-105 hover:border-[#B588FF] inline-flex items-center justify-center">
                Zum Login
              </NavLink>
            </div>
            
            {/* Quick Contact Info */}
            <div className="mt-12 text-gray-600">
              <p>Fragen? Kontaktieren Sie uns direkt:</p>
              <p className="mt-2">
                <a href="tel:+4972175401610" className="hover:text-[#B588FF]">+49 721 75401610</a>
                {' · '}
                <a href="mailto:info@jobdaten.com" className="hover:text-[#B588FF]">info@jobdaten.com</a>
              </p>
            </div>
          </div>
        </section>

        {/* ========================================
            FOOTER MIT LINKS
        ======================================== */}
        <Footer />
      </div>
    </>
  );
}

export default SEOLandingPage;
